// frontend_react/src/components/AdminPanel/cash/TicketsPanel.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  listReceipts,
  fetchReceiptFull,
  fetchReceiptAudit,
  updateReceiptPaymentMeans,
  downloadReceiptPdf,
  sendReceiptEmail,
} from "../../../services/receiptApi";
import PdfEmailModal from "../../common/PdfEmailModal";
import { useAdminScope } from "../../../context/AdminScopeContext";
import { AdminButton } from "../../../pages/admin/action_engine";

const PAYMENT_LABELS = {
  cash: "Espèces",
  card: "Carte",
  check: "Chèque",
  transfer: "Virement",
  crypto: "Crypto",
  giftCheck: "Chèque cadeau",
  titre_resto: "Titre restaurant",
  holidays_check: "Chèque vacances",
  restaurant_ticket: "Ticket restaurant",
  gift_cards: "Carte cadeau",
};

const PAYMENT_OPTIONS = Object.entries(PAYMENT_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export default function TicketsPanel({ embedded = false }) {
  const [receipts, setReceipts] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [audit, setAudit] = useState([]);
  const { selectedCompanyUuid } = useAdminScope();

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [paymentEdit, setPaymentEdit] = useState(null);
  const [newMeans, setNewMeans] = useState("");
  const [reason, setReason] = useState("");

  const totalTickets = receipts.length;

  const totalAmount = useMemo(() => {
    return receipts.reduce(
      (sum, r) => sum + Number(r.amount_including_tax || 0),
      0
    );
  }, [receipts]);

  const loadReceipts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await listReceipts({
        companyUuid: selectedCompanyUuid,
        search: search || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });

      setReceipts(data.receipts || []);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les tickets.");
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyUuid, search, dateFrom, dateTo]);

  const openReceipt = async (uuid) => {
    try {
      setDetailLoading(true);
      setError("");

      const data = await fetchReceiptFull(uuid, {
        companyUuid: selectedCompanyUuid,
      });
      setSelectedReceipt(data);

      try {
        const auditData = await fetchReceiptAudit(uuid, {
          companyUuid: selectedCompanyUuid,
        });
        setAudit(auditData.audit || []);
      } catch {
        setAudit([]);
      }
    } catch (err) {
      console.error(err);
      setError("Impossible de charger le détail du ticket.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadPdf = async (uuid) => {
    try {
      await downloadReceiptPdf(uuid, {
        companyUuid: selectedCompanyUuid,
      });
    } catch (err) {
      console.error(err);
      setError("Impossible de télécharger le PDF.");
    }
  };

  const [emailModalReceipt, setEmailModalReceipt] = useState(null);
  const [emailDefaults, setEmailDefaults] = useState({
    subject: "",
    body: "",
  });

  const openReceiptEmailModal = (receipt) => {
    const today = new Date().toISOString().slice(0, 10);
    const ticketLabel = receipt.number || shortUuid(receipt.uuid);

    setEmailModalReceipt(receipt);
    setEmailDefaults({
      subject: `Queen's 360_Ticket_${ticketLabel}_${today}`,
      body:
        "Bonjour,\n\n" +
        "Veuillez trouver ci-joint votre ticket de caisse.\n\n" +
        `Ticket : ${ticketLabel}\n\n` +
        "Cordialement,\n\n" +
        "L'équipe Queen's 360\n\n",
    });
  };

  const handleSendReceiptEmail = async ({ recipient, cc, subject, body }) => {
    if (!emailModalReceipt) return;

    await sendReceiptEmail(
      emailModalReceipt.uuid,
      {
        recipient,
        cc,
        subject,
        body,
      },
      {
        companyUuid: selectedCompanyUuid,
      }
    );

    setEmailModalReceipt(null);
    setError("");
  };

  const startPaymentEdit = (payment) => {
    setPaymentEdit(payment);
    setNewMeans(payment.means_payment || "");
    setReason("");
  };

  const submitPaymentEdit = async () => {
    if (!selectedReceipt?.receipt?.uuid || !paymentEdit?.uuid) return;

    if (!reason.trim()) {
      setError("Le motif est obligatoire pour la traçabilité NF525.");
      return;
    }

    try {
      setError("");

      await updateReceiptPaymentMeans(
        selectedReceipt.receipt.uuid,
        paymentEdit.uuid,
        {
          means_payment: newMeans,
          reason,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      await openReceipt(selectedReceipt.receipt.uuid);

      setPaymentEdit(null);
      setNewMeans("");
      setReason("");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          "Impossible de modifier le moyen de paiement."
      );
    }
  };

  useEffect(() => {
    setSelectedReceipt(null);
    setAudit([]);
    setPaymentEdit(null);
    setError("");
    loadReceipts();
  }, [selectedCompanyUuid, loadReceipts]);

return (
  <Wrapper>
    {embedded && (
      <Stats>
        <Stat>
          <strong>{totalTickets}</strong>
          <span>tickets</span>
        </Stat>
        <Stat>
          <strong>{totalAmount.toFixed(2)} €</strong>
          <span>total affiché</span>
        </Stat>
      </Stats>
    )}

    <Filters>
      <Input
        placeholder="Rechercher par numéro de ticket"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
      />

      <Input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
      />

      <AdminButton
        type="button"
        variant="primary"
        fullWidth
        loading={loading}
        onClick={loadReceipts}
      >
        Rechercher
      </AdminButton>
    </Filters>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Grid>
        <ListCard>
          <SectionTitle>Historique tickets</SectionTitle>

          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th>N°</th>
                <th>Total TTC</th>
                <th>Hash</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {receipts.map((receipt) => (
                <tr key={receipt.uuid}>
                  <td>{formatDate(receipt.date_issue)}</td>
                  <td>{receipt.number || shortUuid(receipt.uuid)}</td>
                  <td>{Number(receipt.amount_including_tax || 0).toFixed(2)} €</td>
                  <td>
                    {receipt.signature_hash ? (
                      <Badge>Signé</Badge>
                    ) : (
                      <Muted>Non signé</Muted>
                    )}
                  </td>
                  <td>
                    <Actions>
                      <AdminButton
                        type="button"
                        size="small"
                        onClick={() => openReceipt(receipt.uuid)}
                      >
                        Voir
                      </AdminButton>
                      <AdminButton
                        type="button"
                        size="small"
                        onClick={() => handleDownloadPdf(receipt.uuid)}
                      >
                        PDF
                      </AdminButton>
                      <AdminButton
                        type="button"
                        size="small"
                        onClick={() => openReceiptEmailModal(receipt)}
                      >
                        Email
                      </AdminButton>
                    </Actions>
                  </td>
                </tr>
              ))}

              {!receipts.length && !loading && (
                <tr>
                  <td colSpan="5">
                    <Empty>Aucun ticket trouvé.</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ListCard>

        <DetailCard>
          <SectionTitle>Détail ticket</SectionTitle>

          {detailLoading && <Muted>Chargement du détail...</Muted>}

          {!selectedReceipt && !detailLoading && (
            <Empty>Sélectionne un ticket pour afficher le détail.</Empty>
          )}

          {selectedReceipt && (
            <>
              <DetailHeader>
                <div>
                  <strong>
                    Ticket{" "}
                    {selectedReceipt.receipt?.number ||
                      shortUuid(selectedReceipt.receipt?.uuid)}
                  </strong>
                  <span>{formatDate(selectedReceipt.receipt?.date_issue)}</span>
                </div>

                <Total>
                  {Number(
                    selectedReceipt.receipt?.amount_including_tax || 0
                  ).toFixed(2)}{" "}
                  €
                </Total>
              </DetailHeader>

              <MiniTitle>Lignes</MiniTitle>

              <Lines>
                {(selectedReceipt.lines || []).map((line) => (
                  <LineRow key={line.uuid}>
                    <span>{line.wording}</span>
                    <span>x{line.quantity}</span>
                    <strong>
                      {Number(line.amount_including_tax || 0).toFixed(2)} €
                    </strong>
                  </LineRow>
                ))}
              </Lines>

              <MiniTitle>Paiements</MiniTitle>

              <Lines>
                {(selectedReceipt.payments || []).map((payment) => (
                  <LineRow key={payment.uuid}>
                    <span>
                      {PAYMENT_LABELS[payment.means_payment] ||
                        payment.means_payment}
                    </span>
                    <strong>{Number(payment.amount || 0).toFixed(2)} €</strong>
                    <AdminButton
                      type="button"
                      size="small"
                      onClick={() => startPaymentEdit(payment)}
                    >
                      Modifier
                    </AdminButton>
                  </LineRow>
                ))}
              </Lines>

              <MiniTitle>Audit NF525</MiniTitle>

              <AuditBox>
                {audit.map((item) => (
                  <AuditItem key={item.uuid}>
                    <strong>
                      {PAYMENT_LABELS[item.old_means_payment] ||
                        item.old_means_payment}{" "}
                      →{" "}
                      {PAYMENT_LABELS[item.new_means_payment] ||
                        item.new_means_payment}
                    </strong>
                    <span>{item.reason}</span>
                    <small>
                      {item.username || "Utilisateur"} —{" "}
                      {formatDate(item.created_at)}
                    </small>
                  </AuditItem>
                ))}

                {!audit.length && <Muted>Aucune modification auditée.</Muted>}
              </AuditBox>
            </>
          )}
        </DetailCard>
      </Grid>

      {paymentEdit && (
        <Overlay>
          <Modal>
            <ModalTitle>Modifier le moyen de paiement</ModalTitle>

            <Label>Moyen de paiement</Label>
            <Select value={newMeans} onChange={(e) => setNewMeans(e.target.value)}>
              {PAYMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Label>Motif NF525 obligatoire</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Exemple : erreur de saisie lors de l’encaissement."
            />

            <ModalActions>
              <AdminButton
                type="button"
                variant="secondary"
                onClick={() => setPaymentEdit(null)}
              >
                Annuler
              </AdminButton>
              <AdminButton
                type="button"
                variant="primary"
                onClick={submitPaymentEdit}
              >
                Valider la modification
              </AdminButton>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {emailModalReceipt && (
        <PdfEmailModal
          title="Envoyer le ticket"
          description="Le PDF du ticket sera généré et envoyé en pièce jointe."
          defaultTo=""
          defaultCc=""
          defaultSubject={emailDefaults.subject}
          defaultBody={emailDefaults.body}
          onClose={() => setEmailModalReceipt(null)}
          onSend={handleSendReceiptEmail}
        />
      )}
    </Wrapper>
  );
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fr-FR");
  } catch {
    return value;
  }
}

function shortUuid(value) {
  if (!value) return "—";
  return String(value).slice(0, 8);
}

const Wrapper = styled.div`
  display: grid;
  gap: 0.95rem;
  color: #f7f7f7;
  width: 100%;
  padding: 0;
`;

const Header = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 950;
`;

const Subtitle = styled.p`
  margin: 0.35rem 0 0;
  color: rgba(247, 247, 247, 0.72);
  line-height: 1.4;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const Stat = styled.div`
  padding: 0.85rem;
  border-radius: 14px;
  background: rgba(255, 249, 234, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.08);

  strong {
    display: block;
    font-size: 1rem;
    font-weight: 950;
    color: #fff9ea;
  }

  span {
    color: rgba(247, 247, 247, 0.68);
    font-size: 0.78rem;
    font-weight: 750;
  }
`;

const Filters = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 0.75rem;
`;

const Input = styled.input`
  width: 100%;
  min-height: 42px;
  border: none;
  border-radius: 10px;
  padding: 0.75rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 750;
  box-sizing: border-box;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }
`;

const Select = styled.select`
  width: 100%;
  min-height: 42px;
  border: none;
  border-radius: 10px;
  padding: 0.75rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 750;
  box-sizing: border-box;
  outline: none;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  border: none;
  border-radius: 10px;
  padding: 0.75rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 750;
  box-sizing: border-box;
  resize: vertical;
  outline: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.95rem;
`;

const ListCard = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: auto;
`;

const DetailCard = styled(ListCard)``;

const SectionTitle = styled.h3`
  display: inline-block;
  width: fit-content;
  margin: 0 0 0.9rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const MiniTitle = styled.h4`
  margin: 1.2rem 0 0.7rem;
  font-size: 0.95rem;
  font-weight: 950;
  color: #fff9ea;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th {
    text-align: left;
    color: rgba(247, 247, 247, 0.68);
    padding: 0.65rem 0.45rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-weight: 850;
  }

  td {
    padding: 0.65rem 0.45rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
    color: #f7f7f7;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: rgba(80, 180, 110, 0.22);
  color: #baf7c8;
  font-size: 0.72rem;
  font-weight: 900;
`;

const Muted = styled.span`
  color: rgba(247, 247, 247, 0.62);
`;

const Empty = styled.div`
  color: rgba(247, 247, 247, 0.62);
  padding: 1.25rem;
  text-align: center;
  font-weight: 750;
`;

const ErrorBox = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: rgba(180, 40, 40, 0.9);
  color: #fff;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  background: rgba(255, 249, 234, 0.1);
  border-radius: 14px;
  padding: 0.9rem;

  div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  strong {
    color: #fff9ea;
  }

  span {
    color: rgba(247, 247, 247, 0.68);
    font-size: 0.78rem;
  }
`;

const Total = styled.div`
  font-size: 1.25rem;
  font-weight: 950;
  color: #fff9ea;
`;

const Lines = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const LineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.65rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  span {
    color: #f7f7f7;
  }

  strong {
    color: #fff9ea;
  }
`;

const AuditBox = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const AuditItem = styled.div`
  border-radius: 12px;
  padding: 0.75rem;
  background: rgba(255, 249, 234, 0.1);
  color: #f7f7f7;

  strong,
  span,
  small {
    display: block;
  }

  span {
    margin-top: 0.35rem;
  }

  small {
    margin-top: 0.45rem;
    color: rgba(247, 247, 247, 0.62);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  width: min(520px, calc(100vw - 32px));
  background: #202a43;
  color: #f7f7f7;
  border-radius: 18px;
  padding: 1.25rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  color: #fff9ea;
`;

const Label = styled.label`
  display: block;
  font-weight: 850;
  margin: 0.9rem 0 0.45rem;
  color: rgba(247, 247, 247, 0.82);
`;

const ModalActions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  margin-top: 1rem;
`;